
import React, { useState, useEffect } from 'react';

export const CustomerBrandsCarousel: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const customerBrands = [
    {
      name: 'Microsoft',
      logo: 'https://public.readdy.ai/ai/img_res/0cea13a10bc09ad7f4d4e95190250872.jpg',
      industry: 'Technology',
      testimonial: 'Exceptional AI consulting that transformed our cloud infrastructure'
    },
    {
      name: 'Amazon',
      logo: 'https://readdy.ai/api/search-image?query=Amazon%20company%20logo%20clean%20white%20background%20professional%20corporate%20branding%20e-commerce%20technology%20symbol&width=200&height=100&seq=amazon-logo-001&orientation=landscape',
      industry: 'E-commerce',
      testimonial: 'Outstanding automation solutions that streamlined our operations'
    },
    {
      name: 'Google',
      logo: 'https://readdy.ai/api/search-image?query=Google%20company%20logo%20clean%20white%20background%20professional%20corporate%20branding%20search%20engine%20technology%20symbol&width=200&height=100&seq=google-logo-001&orientation=landscape',
      industry: 'Technology',
      testimonial: 'Innovative AI strategies that enhanced our machine learning capabilities'
    },
    {
      name: 'Apple',
      logo: 'https://readdy.ai/api/search-image?query=Apple%20company%20logo%20clean%20white%20background%20professional%20corporate%20branding%20technology%20consumer%20electronics%20symbol&width=200&height=100&seq=apple-logo-001&orientation=landscape',
      industry: 'Consumer Electronics',
      testimonial: 'Revolutionary AI implementation that improved user experience'
    },
    {
      name: 'Tesla',
      logo: 'https://readdy.ai/api/search-image?query=Tesla%20company%20logo%20clean%20white%20background%20professional%20corporate%20branding%20electric%20vehicle%20automotive%20technology%20symbol&width=200&height=100&seq=tesla-logo-001&orientation=landscape',
      industry: 'Automotive',
      testimonial: 'Advanced AI solutions that accelerated our autonomous driving development'
    },
    {
      name: 'Netflix',
      logo: 'https://readdy.ai/api/search-image?query=Netflix%20company%20logo%20clean%20white%20background%20professional%20corporate%20branding%20streaming%20entertainment%20technology%20symbol&width=200&height=100&seq=netflix-logo-001&orientation=landscape',
      industry: 'Entertainment',
      testimonial: 'Intelligent recommendation systems that boosted user engagement'
    },
    {
      name: 'Salesforce',
      logo: 'https://readdy.ai/api/search-image?query=Salesforce%20company%20logo%20clean%20white%20background%20professional%20corporate%20branding%20CRM%20cloud%20technology%20symbol&width=200&height=100&seq=salesforce-logo-001&orientation=landscape',
      industry: 'CRM Software',
      testimonial: 'Powerful AI integration that enhanced our customer relationship management'
    },
    {
      name: 'IBM',
      logo: 'https://readdy.ai/api/search-image?query=IBM%20company%20logo%20clean%20white%20background%20professional%20corporate%20branding%20enterprise%20technology%20consulting%20symbol&width=200&height=100&seq=ibm-logo-001&orientation=landscape',
      industry: 'Enterprise Technology',
      testimonial: 'Comprehensive AI consulting that modernized our enterprise systems'
    }
  ];

  const itemsPerSlide = 4;
  const totalSlides = Math.ceil(customerBrands.length / itemsPerSlide);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 4000);

    return () => clearInterval(interval);
  }, [totalSlides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="w-6 h-2 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full mr-3 shadow-lg"></div>
            <span className="text-blue-600 font-semibold uppercase tracking-wider">Trusted Partners</span>
            <div className="w-6 h-2 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full ml-3 shadow-lg"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Trusted by <span className="text-blue-600">Industry Leaders</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're proud to partner with Fortune 500 companies and innovative startups worldwide, delivering AI solutions that drive real business results.
          </p>
        </div>

        <div className="relative">
          {/* Carousel Container */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="w-full flex-shrink-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 p-8">
                    {customerBrands
                      .slice(slideIndex * itemsPerSlide, (slideIndex + 1) * itemsPerSlide)
                      .map((brand, index) => (
                        <div 
                          key={index} 
                          className="group bg-white rounded-xl p-6 hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200"
                        >
                          <div className="text-center">
                            <div className="w-full h-20 flex items-center justify-center mb-4 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                              <img 
                                src={brand.logo} 
                                alt={`${brand.name} logo`}
                                className="max-w-full max-h-16 object-contain filter group-hover:brightness-110 transition-all"
                              />
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                              {brand.name}
                            </h3>
                            <p className="text-sm text-blue-600 font-medium mb-3">
                              {brand.industry}
                            </p>
                            <p className="text-sm text-gray-600 italic leading-relaxed">
                              "{brand.testimonial}"
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center text-blue-600 hover:bg-blue-600 hover:text-white group z-10"
          >
            <i className="ri-arrow-left-line text-xl group-hover:scale-110 transition-transform"></i>
          </button>
          
          <button 
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center text-blue-600 hover:bg-blue-600 hover:text-white group z-10"
          >
            <i className="ri-arrow-right-line text-xl group-hover:scale-110 transition-transform"></i>
          </button>
        </div>

        {/* Dots Navigation */}
        <div className="flex justify-center mt-8 space-x-2">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentSlide === index 
                  ? 'bg-blue-600 w-8' 
                  : 'bg-gray-300 hover:bg-blue-400'
              }`}
            />
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-building-line text-white text-2xl"></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-2">500+</h3>
            <p className="text-gray-600">Enterprise Clients</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-global-line text-white text-2xl"></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-2">50+</h3>
            <p className="text-gray-600">Countries Served</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-award-line text-white text-2xl"></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-2">98%</h3>
            <p className="text-gray-600">Client Satisfaction</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-rocket-line text-white text-2xl"></i>
            </div>
            <h3 className="text-3xl font-bold text-gray-800 mb-2">1000+</h3>
            <p className="text-gray-600">AI Projects Delivered</p>
          </div>
        </div>
      </div>
    </section>
  );
};
